/* eslint-disable react/require-default-props */

import { ButtonHTMLAttributes, forwardRef, isValidElement, memo, ReactNode, useImperativeHandle, useState } from "react"

import createStyle, { CustomTagArgs } from "@josephmark/createstyle"
import * as RDropdownMenu from "@radix-ui/react-dropdown-menu"
import clsx from "clsx"

import { s } from "@/utils/i18n"

import { Link } from "@/components/Link"
import { P3 } from "@/components/TypeRamp"

import ExpanderRightIcon from "@/icons/ExpanderRight"
import MeatballsIcon from "@/icons/Meatballs"

const THEMES = {
  kebab: clsx(
    "h-6 w-6",
    "inline-flex items-center justify-center rounded-sm",
    "disabled:opacity-20",
    "hover:bg-black/5 aria-expanded:bg-black/5 dark:hover:bg-black dark:aria-expanded:bg-black",
    "transition duration-300"
  ),
  simple: clsx("h-9 w-9", "inline-flex items-center justify-center rounded-sm"),
  custom: "",
}

const ITEM_THEMES = {
  default: "flex gap-1",
  destructive: "text-red-600 flex gap-1",
}

export type DropdownMenuItemProps = {
  label: ReactNode
  theme?: keyof typeof ITEM_THEMES
  childActions?: DropdownMenuItemProps[]
  subProps?: RDropdownMenu.DropdownMenuSubContentProps
} & Omit<CustomTagArgs, "label">

export type DropdownMenuProps = {
  actions: ("divider" | DropdownMenuItemProps | ReactNode)[] | ReactNode
  theme?: keyof typeof THEMES
  contentProps?: Omit<
    RDropdownMenu.DropdownMenuContentProps,
    "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart"
  >

  onOpenChange?: (open: boolean) => void
} & ButtonHTMLAttributes<HTMLButtonElement> &
  Pick<RDropdownMenu.DropdownMenuContentProps, "align" | "alignOffset">

export const MenuItemBase = createStyle(
  "button",
  clsx(
    "text-left",
    "p-2",
    "flex items-center",
    P3.classNames,
    "outline-none hover:bg-black/5 focus:bg-black/5 aria-expanded:bg-black/5 dark:hover:bg-white/10 dark:focus:bg-white/10 dark:aria-expanded:bg-white/10",
    "disabled:opacity-30 disabled:hover:bg-transparent",
    "cursor-pointer",
    "transition duration-300",
    "rounded-lg",
    "w-full"
  )
)

export const MenuContent = createStyle(
  "div",
  "border-empress/20 dark:bg-slate animate-fade-in z-50 flex w-[220px] flex-col rounded-xl border bg-white p-1 text-black dark:text-white",
  { style: { boxShadow: "0 4px 8px rgba(0,0,0, 0.08)" } }
)

const MaybeLink = forwardRef<HTMLAnchorElement, DropdownMenuItemProps>(
  ({ href, children, className, label, ...props }, ref) => {
    if (href) {
      return (
        <Link href={href} className={clsx(className, MenuItemBase.classNames)} ref={ref} {...props}>
          {label || children}
        </Link>
      )
    }

    return (
      <MenuItemBase ref={ref} className={className} {...props}>
        {label || children}
      </MenuItemBase>
    )
  }
)

const MenuItem = ({
  theme = "default",
  childActions = [],
  subProps,
  children,
  label,
  ...props
}: DropdownMenuItemProps) => {
  if (childActions?.length > 0 || subProps) {
    return (
      <RDropdownMenu.Sub>
        <RDropdownMenu.SubTrigger asChild>
          <MenuItemBase as="div" className={clsx(ITEM_THEMES[theme])} {...props}>
            <span className="grow">{label || children}</span>
            <ExpanderRightIcon className="opacity-40" />
          </MenuItemBase>
        </RDropdownMenu.SubTrigger>
        <RDropdownMenu.Portal>
          <RDropdownMenu.SubContent className={MenuContent.classNames} {...subProps}>
            {childActions?.map((child) => (
              <MenuItem {...child} />
            ))}
            {subProps?.children}
          </RDropdownMenu.SubContent>
        </RDropdownMenu.Portal>
      </RDropdownMenu.Sub>
    )
  }
  return (
    <RDropdownMenu.Item className="group" asChild>
      <MaybeLink href={props.href} className={clsx(ITEM_THEMES[theme])} label={label} {...props} />
    </RDropdownMenu.Item>
  )
}

/**
 * Creates a screen-aware dropdown menu. Provide trigger button children or defaults to a kebab / more icon.
 *
 * @param actions An array of actions to render in the dropdown menu. Use the `divider` string to render a divider.
 * @param theme The theme of the dropdown menu. Defaults to `kebab`.
 * @param children The children of the trigger button. Defaults to a kebab / more icon.
 */
export const DropdownMenu = forwardRef<{ setIsOpen: (open: boolean) => void }, DropdownMenuProps>(
  (
    {
      actions = [],
      className,
      children,
      contentProps = {},
      onOpenChange = (_o) => {},
      theme = "kebab",
      align = "end",
      alignOffset,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)

    useImperativeHandle(ref, () => ({ setIsOpen }))

    return (
      <RDropdownMenu.Root
        modal={false}
        open={isOpen}
        onOpenChange={(o) => {
          setIsOpen(o)
          onOpenChange(o)
        }}
      >
        <RDropdownMenu.Trigger className={clsx(className, "cursor-pointer", THEMES[theme])} {...props}>
          {children || (
            <>
              <MeatballsIcon width="20" height="20" />
              <span className="sr-only">{s`Open Menu`}</span>
            </>
          )}
        </RDropdownMenu.Trigger>
        <RDropdownMenu.Portal>
          {/* <AnimatePresence> */}
          <RDropdownMenu.Content
            className={MenuContent.classNames}
            style={{ boxShadow: "0 4px 8px 0 rgba(0,0,0, 0.08)" }}
            align={align}
            sideOffset={2}
            alignOffset={alignOffset}
            {...contentProps}
          >
            {Array.isArray(actions)
              ? actions.map((action, idx) => {
                  if (action === "divider") {
                    return (
                      <RDropdownMenu.Separator key={idx} className="my-1 w-full border-t border-current opacity-20" />
                    )
                  }
                  if (isValidElement(action)) {
                    return action
                  }
                  const _a = action as DropdownMenuItemProps
                  return <MenuItem key={typeof _a.label === "string" ? _a.label : idx} {..._a} />
                })
              : actions}
          </RDropdownMenu.Content>
          {/* </AnimatePresence> */}
        </RDropdownMenu.Portal>
      </RDropdownMenu.Root>
    )
  }
)

export default memo(DropdownMenu)
